import React, { useEffect, useState } from 'react';
import axiosInstance from '../Core/RestHelper';
import { TeamMember } from '../Model/Teams';


interface AwsClient {
  id: number;
  role_arn: string;
  region: string;
}

interface GrantAwsAccessModalProps {
  isOpen: boolean;
  member: TeamMember;
  onClose: () => void;
}

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(5,5,8,.7)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};

const modalStyle: React.CSSProperties = {
  background: '#111116', border: '1px solid #26262f', borderRadius: '12px',
  padding: '22px', width: '440px', maxWidth: '90vw', fontFamily: "'Inter',sans-serif",
};

export const GrantAwsAccessModal: React.FC<GrantAwsAccessModalProps> = ({ isOpen, member, onClose }) => {
  const [clients, setClients] = useState<AwsClient[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<Record<number, string>>({});
  const [grantedIds, setGrantedIds] = useState<Set<number>>(new Set());
  const [grantingId, setGrantingId] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchClients();
      setGrantedIds(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchClients = async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      // NOTE: assumes GET /aws/clients-list/ — adjust the path here if your
      // AWS router is mounted under a different prefix
      const res = await axiosInstance.get('/aws/clients-list/');
      setClients(res.data.clients || []);
    } catch (error) {
      console.error('Error fetching AWS clients:', error);
      setLoadError('Could not load AWS accounts.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGrant = async (clientId: number) => {
    setGrantingId(clientId);
    try {
      await axiosInstance.post('/teams/access/aws', {
        team_member_id: member.id,
        aws_client_id: clientId,
        permission: permissions[clientId] || 'deploy',
      });
      setGrantedIds(prev => new Set(prev).add(clientId));
    } catch (error) {
      console.error('Error granting access:', error);
    } finally {
      setGrantingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: '16px', fontWeight: 700, color: '#eeeef5', marginBottom: '4px' }}>
          AWS access — {member.username}
        </div>
        <p style={{ fontSize: '12.5px', color: '#8e8ea8', marginBottom: '16px' }}>
          Choose which AWS accounts {member.username} can deploy to.
        </p>

        {isLoading ? (
          <div style={{ fontSize: '12.5px', color: '#52525f', padding: '20px 0', textAlign: 'center' as const }}>Loading AWS accounts…</div>
        ) : loadError ? (
          <div style={{ fontSize: '12.5px', color: '#f87171', padding: '20px 0', textAlign: 'center' as const }}>{loadError}</div>
        ) : clients.length === 0 ? (
          <div style={{ fontSize: '12.5px', color: '#52525f', padding: '20px 0', textAlign: 'center' as const }}>No AWS accounts connected yet</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px', maxHeight: '280px', overflowY: 'auto' as const }}>
            {clients.map(client => (
              <div key={client.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: '#17171e', border: '1px solid #26262f', borderRadius: '7px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', color: '#eeeef5', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                    {client.role_arn}
                  </div>
                  <div style={{ fontSize: '11px', color: '#52525f' }}>{client.region}</div>
                </div>
                <select
                  value={permissions[client.id] || 'deploy'}
                  onChange={e => setPermissions(prev => ({ ...prev, [client.id]: e.target.value }))}
                  style={{ background: '#1e1e27', color: '#eeeef5', border: '1px solid #32323d', borderRadius: '5px', fontSize: '11.5px', padding: '4px 6px' }}
                >
                  <option value="view">View</option>
                  <option value="deploy">Deploy</option>
                  <option value="admin">Admin</option>
                </select>
                <button
                  onClick={() => handleGrant(client.id)}
                  disabled={grantingId === client.id}
                  style={{
                    padding: '5px 10px', borderRadius: '6px', fontSize: '11.5px', fontWeight: 500,
                    cursor: 'pointer', border: 'none', fontFamily: "'Inter',sans-serif",
                    background: grantedIds.has(client.id) ? 'rgba(34,197,94,.12)' : '#6366f1',
                    color: grantedIds.has(client.id) ? '#22c55e' : '#fff',
                  }}
                >
                  {grantingId === client.id ? '…' : grantedIds.has(client.id) ? 'Granted ✓' : 'Grant'}
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ fontSize: '11px', color: '#52525f', marginBottom: '14px' }}>
          Granting again with a different permission level updates the existing grant. This list doesn't yet show
          access already granted in a previous session — there's no endpoint for that yet.
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            style={{ padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', border: '1px solid #32323d', background: '#17171e', color: '#eeeef5', fontFamily: "'Inter',sans-serif" }}
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};